/**
 * The sign-in screens on a touch screen (docs/design/visual-overhaul.md
 * §12.7 C3): the on-screen keyboard never hides the form's submit, and no
 * field takes the focus by itself (the keyboard would jump up as the screen
 * opens; the person taps the field they want).
 */
import { readLayout } from '@neosocial/ui/layout';
import { useEffect, type RefObject } from 'react';

/** True where a field may take the focus by itself: a mouse and keyboard. */
export function autoFocusOk(): boolean {
  return readLayout().pointer === 'fine';
}

/** How long the keyboard takes to come up before the form is fitted (iOS animates it). */
export const KEYBOARD_SETTLE_MS = 350;

/**
 * Scrolls the focused field's form so its submit shows, then the field
 * itself (the field wins when both do not fit). Only for a finger.
 */
export function fitSubmit(field: Element | null): void {
  if (!field || readLayout().pointer !== 'coarse') return;
  const submit = field.closest('form')?.querySelector<HTMLElement>('.ns-auth-submit');
  submit?.scrollIntoView?.({ block: 'nearest' });
  field.scrollIntoView?.({ block: 'nearest' });
}

/**
 * Follows `root` (the auth screen, its scroller): when a field takes the
 * focus, and whenever the visual viewport changes (the keyboard comes up or
 * goes), the field and its form's submit are scrolled into view.
 */
export function useSubmitInView(root: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fit = () => {
      const a = document.activeElement;
      if (a && el.contains(a) && a.matches('input, textarea, select')) fitSubmit(a);
    };
    const onFocus = (e: FocusEvent) => {
      if (!(e.target instanceof Element) || !e.target.matches('input, textarea, select')) return;
      clearTimeout(timer);
      timer = setTimeout(fit, KEYBOARD_SETTLE_MS);
    };
    el.addEventListener('focusin', onFocus);
    vv?.addEventListener('resize', fit);
    return () => {
      clearTimeout(timer);
      el.removeEventListener('focusin', onFocus);
      vv?.removeEventListener('resize', fit);
    };
  }, [root]);
}
